import { useEffect, useMemo, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Trans, msg } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import type { DocumentReferenceLink, IntakeData, UploadedMedia } from "@/lib/types";
import {
  ExternalLink,
  Eye,
  File as FileIcon,
  FileImage,
  FileText,
  Link2,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";

function formatSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function isUploadedMedia(u: File | UploadedMedia): u is UploadedMedia {
  return !(u instanceof File) && typeof u === "object" && u !== null && "downloadUrl" in u;
}

function isImageItem(item: File | UploadedMedia): boolean {
  if (item instanceof File) return item.type.startsWith("image/");
  const mt = (item.mimeType || "").toLowerCase();
  const media = (item.mediaType || "").toLowerCase();
  return mt.startsWith("image/") || media.includes("image");
}

function getDisplayName(item: File | UploadedMedia): string {
  if (item instanceof File) return item.name;
  return item.filename || "";
}

function getSizeBytes(item: File | UploadedMedia): number | null {
  if (item instanceof File) return item.size;
  return typeof item.size === "number" ? item.size : null;
}

function getMime(item: File | UploadedMedia): string {
  if (item instanceof File) return item.type || "";
  return item.mimeType || "";
}

type DocKind = "pdf" | "dicom" | "image" | "other";

function classifyDoc(name: string, mime: string): DocKind {
  const m = mime.toLowerCase();
  const n = name.toLowerCase();
  if (m.startsWith("image/")) return "image";
  if (m === "application/pdf" || n.endsWith(".pdf")) return "pdf";
  if (
    m === "application/dicom" ||
    m.includes("dicom") ||
    n.endsWith(".dcm")
  ) {
    return "dicom";
  }
  return "other";
}

function linkHostname(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}

function truncateMiddle(s: string, max: number) {
  if (s.length <= max) return s;
  const half = Math.floor((max - 3) / 2);
  return `${s.slice(0, half)}…${s.slice(-half)}`;
}

function DocThumbnailIcon({ kind }: { kind: DocKind }) {
  if (kind === "pdf") {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center gap-1 rounded-lg bg-red-50 p-2">
        <FileText className="h-8 w-8 text-red-600/90" strokeWidth={1.75} />
        <span className="text-[9px] font-bold uppercase tracking-wide text-red-700/80">
          PDF
        </span>
      </div>
    );
  }
  if (kind === "dicom") {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center gap-1 rounded-lg bg-slate-100 p-2">
        <FileIcon className="h-8 w-8 text-slate-600" strokeWidth={1.75} />
        <span className="text-[9px] font-bold uppercase tracking-wide text-slate-600">
          DICOM
        </span>
      </div>
    );
  }
  return (
    <div className="flex h-full w-full items-center justify-center rounded-lg bg-gray-100 p-2">
      <FileText className="h-9 w-9 text-gray-400" strokeWidth={1.5} />
    </div>
  );
}

function UploadPreviewTile({
  item,
  onOpenImage,
}: {
  item: File | UploadedMedia;
  onOpenImage: (src: string, title: string) => void;
}) {
  const { _ } = useLingui();
  const name = getDisplayName(item) || _(msg`Unnamed file`);
  const size = getSizeBytes(item);
  const mime = getMime(item);
  const image = isImageItem(item);

  const previewUrl = useMemo(() => {
    if (!image) return null;
    if (item instanceof File) return URL.createObjectURL(item);
    return item.downloadUrl?.trim() || null;
  }, [item, image]);

  useEffect(() => {
    if (!previewUrl || !(item instanceof File)) return;
    return () => URL.revokeObjectURL(previewUrl);
  }, [previewUrl, item]);

  const kind = classifyDoc(name, mime);

  if (image && previewUrl) {
    return (
      <button
        type="button"
        onClick={() => onOpenImage(previewUrl, name)}
        className="group !m-0 w-full overflow-hidden rounded-xl border border-gray-200 bg-white text-left shadow-[0_1px_2px_rgba(15,23,42,0.04)] transition-shadow hover:shadow-md"
      >
        <div className="relative aspect-square w-full overflow-hidden bg-gray-100">
          <img
            src={previewUrl}
            alt=""
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 flex items-center justify-center bg-black/35 opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
            <Eye className="h-5 w-5 text-white" />
          </div>
        </div>
        <p className="truncate px-2 py-2 text-center text-[11px] font-medium leading-tight text-gray-800">
          {name}
        </p>
      </button>
    );
  }

  if (image && !previewUrl) {
    return (
      <div className="overflow-hidden rounded-xl border border-amber-100 bg-amber-50/80 p-2 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
        <div className="flex aspect-square w-full flex-col items-center justify-center gap-1 rounded-lg border border-amber-200/80 bg-white">
          <FileImage className="h-8 w-8 text-amber-600/70" />
          <span className="px-2 text-center text-[10px] font-medium text-amber-900/80">
            <Trans>Preview unavailable</Trans>
          </span>
        </div>
        <p className="truncate px-1 py-2 text-center text-[11px] font-medium text-gray-800">
          {name}
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-gray-50/80 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
      <div className="aspect-square w-full overflow-hidden p-2">
        <DocThumbnailIcon kind={kind} />
      </div>
      <div className="border-t border-gray-100 bg-white px-2.5 py-2">
        <p className="truncate text-[11px] font-medium leading-snug text-gray-800">
          {name}
        </p>
        {size != null && (
          <p className="mt-0.5 text-[10px] text-gray-400">{formatSize(size)}</p>
        )}
      </div>
    </div>
  );
}

function Subheading({ children }: { children: ReactNode }) {
  return (
    <h4 className="mb-2.5 text-[11px] font-bold uppercase tracking-wide text-gray-500">
      {children}
    </h4>
  );
}

export interface ReviewDocumentsUploadsSectionProps {
  uploads?: IntakeData["uploads"];
  documentReferenceLinks?: DocumentReferenceLink[];
  onEdit: () => void;
}

export function ReviewDocumentsUploadsSection({
  uploads,
  documentReferenceLinks,
  onEdit,
}: ReviewDocumentsUploadsSectionProps) {
  const { _ } = useLingui();
  const labs = uploads?.labsImaging ?? [];
  const photos = uploads?.photos ?? [];
  const idDocs = uploads?.idPassport ?? [];
  const links = (documentReferenceLinks ?? []).filter((l) => l?.url?.trim());

  const [lightbox, setLightbox] = useState<{
    src: string;
    title: string;
  } | null>(null);

  const hasAny =
    labs.length > 0 || photos.length > 0 || idDocs.length > 0 || links.length > 0;
  if (!hasAny) return null;

  const modal =
    lightbox &&
    typeof document !== "undefined" &&
    createPortal(
      <div
        className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 p-4"
        onClick={() => setLightbox(null)}
        role="presentation"
      >
        <div
          className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-label={_(msg`Document preview`)}
        >
          <div className="flex flex-shrink-0 items-center justify-between border-b border-gray-100 p-4 sm:p-5">
            <h3 className="min-w-0 flex-1 truncate text-base font-semibold text-gray-900">
              {lightbox.title}
            </h3>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="rounded-xl"
              onClick={() => setLightbox(null)}
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-5">
            <div
              className="flex items-center justify-center overflow-hidden rounded-xl bg-gray-100"
              style={{ maxHeight: "calc(90vh - 140px)" }}
            >
              <img
                src={lightbox.src}
                alt=""
                className="max-h-full max-w-full object-contain"
              />
            </div>
          </div>
        </div>
      </div>,
      document.body,
    );

  return (
    <>
      {modal}
      <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 bg-gray-50 px-4 py-3 sm:px-5">
          <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500">
            <Trans>Documents & Uploads</Trans>
          </h3>
          <button
            type="button"
            onClick={onEdit}
            className="text-xs font-semibold text-[#166B93] underline-offset-2 hover:underline"
          >
            <Trans>Edit</Trans>
          </button>
        </div>

        <div className="space-y-5 px-4 py-4 sm:px-5 sm:py-5">
          {labs.length > 0 && (
            <div>
              <Subheading>
                <Trans>Lab results & imaging</Trans>
              </Subheading>
              <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 sm:gap-3">
                {labs.map((item, i) => (
                  <UploadPreviewTile
                    key={
                      isUploadedMedia(item)
                        ? `lab-${item.id}`
                        : `lab-file-${i}-${item instanceof File ? item.name + item.lastModified : ""}`
                    }
                    item={item}
                    onOpenImage={(src, title) => setLightbox({ src, title })}
                  />
                ))}
              </div>
            </div>
          )}

          {photos.length > 0 && (
            <div>
              <Subheading>
                <Trans>Additional photos</Trans>
              </Subheading>
              <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 sm:gap-3">
                {photos.map((item, i) => (
                  <UploadPreviewTile
                    key={
                      isUploadedMedia(item)
                        ? `photo-${item.id}`
                        : `photo-file-${i}-${item instanceof File ? item.name + item.lastModified : ""}`
                    }
                    item={item}
                    onOpenImage={(src, title) => setLightbox({ src, title })}
                  />
                ))}
              </div>
            </div>
          )}

          {idDocs.length > 0 && (
            <div>
              <Subheading>
                <Trans>Identification</Trans>
              </Subheading>
              <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 sm:gap-3">
                {idDocs.map((item, i) => (
                  <UploadPreviewTile
                    key={
                      isUploadedMedia(item)
                        ? `id-${item.id}`
                        : `id-file-${i}-${item instanceof File ? item.name + item.lastModified : ""}`
                    }
                    item={item}
                    onOpenImage={(src, title) => setLightbox({ src, title })}
                  />
                ))}
              </div>
            </div>
          )}

          {links.length > 0 && (
            <div>
              <Subheading>
                <Trans>Document links</Trans>
              </Subheading>
              <ul className="space-y-2">
                {links.map((link, i) => {
                  const url = link.url.trim();
                  const label =
                    link.label?.trim() || linkHostname(url) || url;
                  return (
                    <li key={`${url}-${i}`}>
                      <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-start gap-3 rounded-xl border border-gray-200 bg-gray-50/80 px-3.5 py-3 transition-colors hover:border-[#166B93]/30 hover:bg-[#166B93]/5"
                      >
                        <div className="mt-0.5 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
                          <Link2 className="h-4 w-4 text-[#166B93]" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium text-gray-900">
                            {label}
                          </p>
                          <p
                            className="mt-0.5 break-all text-xs text-gray-500"
                            title={url}
                          >
                            {truncateMiddle(url, 72)}
                          </p>
                        </div>
                        <ExternalLink className="mt-1 h-4 w-4 flex-shrink-0 text-gray-400" />
                      </a>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
